// The module's copy, in English. Exactly the same keys as es.js.

export default {
  markets: {
    title: 'Markets',

    actions: {
      save: 'Save',
      saved: 'Saved',
      cancel: 'Cancel',
      edit: 'Edit',
      remove: 'Remove',
    },

    units: {
      points: 'pts',
    },

    windows: {
      day: 'One day',
      week: 'One week',
      month: '30 days',
      year: 'One year',
    },

    home: {
      title: 'Markets',
      subtitle: 'What is changing outside, and what it costs you',
      prepareMeeting: 'Prepare a meeting',
      risks: 'Open risks',
      opportunities: 'Opportunities',
      watched: 'Watched factors',
      newReports: 'New reports',
      exposure: 'Exposed spend',
      costAtRisk: 'Extra cost a year',
      whatChanged: 'What has changed',
      summarise: 'Summarise with AI',
      summaryUnavailable: 'The summary is not available right now. The changes below are the same either way.',
      nothingChanged: 'Nothing has moved',
      nothingChangedHint: 'No watched factor has crossed its threshold since you were last here.',
      references: '{count} references',
      radar: 'What is moving most',
      radarQuiet: 'No watched factor is outside its normal range.',
      unlinked: '{count} indicators are not tied to anything of yours: those produce no figure in euros.',
      stale: '{count} indicators have not been updated for a while.',
      toDo: 'Waiting for somebody',
      sourcesDue: 'Sources to check',
      decisionsDue: 'Decisions to review',
      overdue: '{count} days overdue',
      nothingToDo: 'Nothing is waiting.',
      reports: 'Recent reports',
      kind: {
        impact: 'Impact',
        indicator: 'Indicator',
        report: 'Report',
      },
    },

    coverage: {
      title: 'No internal data yet',
      body: 'This module measures what happens outside against your own consumption, purchases and costs. With neither Warehouse nor Purchasing installed there is nothing to measure it against.',
      hint: 'The outside half — topics, sources, reports and indicators — works from today.',
    },

    radar: {
      title: 'Radar',
      subtitle: 'Everything you watch, with its colour',
      recompute: 'Recompute now',
      recomputed: 'Recomputed: {count} impacts',
      colFactor: 'Factor',
      colValue: 'Value',
      colChange: 'Change',
      colStatus: 'Status',
      colCost: 'Cost a year',
      noLinks: 'Not linked',
      notUpdated: 'Not updated',
      unlinked: '{count} not linked to anything of yours',
      stale: '{count} not updated',
      empty: 'The radar is empty',
      emptyHint: 'Add an indicator and paste its series so it starts being watched.',
    },

    status: {
      favourable: 'Favourable',
      neutral: 'Normal',
      watch: 'Watch',
      risk: 'Risk',
      critical: 'Critical',
      unknown: 'No data',
      up: 'Up',
      down: 'Down',
      flat: 'Flat',
    },

    severity: {
      info: 'Information',
      watch: 'Watch',
      high: 'Important',
      critical: 'Critical',
    },

    direction: {
      risk: 'Risk',
      opportunity: 'Opportunity',
      both: 'Risks and opportunities',
    },

    trend: {
      up: 'Rising',
      down: 'Falling',
      flat: 'Flat',
      unknown: 'Not enough series',
    },

    kinds: {
      commodity: 'Raw materials',
      energy: 'Energy',
      logistics: 'Transport and logistics',
      industry: 'Industrial activity',
      construction: 'Construction',
      economy: 'Economy',
      fx: 'Currencies',
      labour: 'Labour',
      sector: 'Sector',
    },

    valueTypes: {
      price: 'Price',
      index: 'Index',
      rate: 'Rate or percentage',
      quantity: 'Quantity',
    },

    badDirection: {
      up: 'Going up hurts us',
      down: 'Going down hurts us',
      none: 'Neither direction hurts us',
    },

    origins: {
      manual: 'By hand',
      import: 'Pasted',
      report: 'From a report',
      feed: 'Automatic',
    },

    horizons: {
      short: 'Short term',
      medium: 'Medium term',
      long: 'Long term',
      unknown: 'No horizon',
    },

    importance: {
      1: 'Low',
      2: 'Medium',
      3: 'High',
    },

    strength: {
      none: 'no',
      weak: 'weak',
      moderate: 'moderate',
      strong: 'strong',
    },

    confidence: {
      high: 'High confidence',
      medium: 'Medium confidence',
      low: 'Low confidence',
      short: 'Confidence {score}',
    },

    metrics: {
      revenue: 'Revenue',
      order_intake: 'Work won',
      offers_out: 'Quoted',
      purchase_spend: 'Goods received',
      material_cost: 'Material consumed',
    },

    tiers: {
      A: 'Official body: national statistics, Eurostat, central banks, the European Commission',
      B: 'Trade association or recognised consultancy',
      C: 'Specialist press',
      D: 'Unverified',
    },

    sourceKinds: {
      website: 'Website',
      rss: 'RSS',
      pdf: 'PDF',
      report: 'Report',
      api: 'API',
      spreadsheet: 'Spreadsheet',
      newsletter: 'Newsletter',
      association: 'Trade association',
      public_body: 'Public body',
      supplier: 'Supplier',
      institute: 'Economic institute',
      bank: 'Bank',
      consultancy: 'Consultancy',
      manual: 'By hand',
    },

    indicators: {
      title: 'Indicators',
      subtitle: 'What you watch outside, with its series',
      new: 'New indicator',
      edit: 'Edit indicator',
      searchPlaceholder: 'Name or key…',
      allKinds: 'All categories',
      colName: 'Indicator',
      colValue: 'Latest value',
      colMonth: '30 days',
      colYear: 'One year',
      colLinks: 'Linked',
      linked: '{count} links',
      unlinked: 'Not linked',
      empty: 'No indicators yet',
      emptyCommodities: 'No raw materials yet',
      emptyHint: 'Add the first one and paste its price series; everything else comes out of that.',
      name: 'Name',
      namePlaceholder: 'Hot-rolled steel (Europe)',
      kind: 'Category',
      valueType: 'What the value is',
      valueTypeHint: 'A price reads as a percentage; a rate such as inflation reads in points.',
      unit: 'Unit',
      unitHint: 'What goes after the number.',
      unitPlaceholder: 'EUR/kg',
      source: 'Source',
      noSource: 'No source',
      badDirection: 'Which direction hurts you',
      badDirectionHint: 'Steel going up hurts a fabricator; electricity going down helps them. Without this the radar colours the board backwards.',
      watchThreshold: 'Watch threshold (%)',
      alertThreshold: 'Risk threshold (%)',
      thresholdHint: 'Over 30 days. Empty uses the module default.',
      default: 'Default',
      description: 'Description',
    },

    commodities: {
      title: 'Raw materials',
      subtitle: 'What you buy, and how much its price moves',
    },

    indicator: {
      subtitle: '{kind} · updated {updated}',
      current: 'Now',
      trend: 'Trend',
      series: 'Series',
      from: '{source} · tier {tier}',
      noSeries: 'Not enough readings yet to draw anything.',
      addReading: 'Add a reading',
      links: 'Links',
      simulate: 'Simulate',
      linkedTo: 'What it affects',
      noLinks: 'Not tied to anything of yours',
      noLinksHint: 'Without links this indicator is information, not euros. Tie it to the references that depend on it.',
      readings: 'Readings',
      noReadings: 'No readings yet',
      noReadingsHint: 'Add one by hand, or paste the whole series out of a spreadsheet.',
      colDate: 'Date',
      colValue: 'Value',
      colOrigin: 'Where it came from',
      fromReport: 'Open the report',
      removeReading: 'Remove this reading?',
      removeReadingHint: 'The series is recomputed without it. The change stays in the audit trail.',
    },

    points: {
      title: 'Readings for {name}',
      tabOne: 'One reading',
      tabMany: 'Paste a series',
      date: 'Date',
      value: 'Value {unit}',
      valuePlaceholder: '1.29',
      note: 'Note',
      noteHint: 'Where this particular figure came from, if it was not the usual source.',
      save: 'Save the reading',
      saved: 'Reading saved',
      paste: 'Paste two columns: date and value',
      pasteHint: 'Exactly as they come out of the spreadsheet or the page. It understands both decimal separators, and dates in both formats.',
      pastePlaceholder: '01/01/2026;1.18\n01/02/2026;1.21',
      import: 'Import',
      imported: '{count} readings saved',
      rejected: '{count} lines could not be read',
      line: 'Line {line}',
      reason: {
        columns: 'does not have two columns',
        date: 'the date cannot be read',
        value: 'the value cannot be read',
      },
    },

    links: {
      title: 'Links for {name}',
      lead: 'A link says how much of a reference\'s cost this indicator explains. Without links there is no figure in euros anywhere.',
      tabCurrent: 'Current',
      tabAdd: 'Add',
      tabSuggest: 'Propose with AI',
      none: 'No links yet',
      noneHint: 'Tie this indicator to the references or families that depend on it.',
      colTarget: 'What it affects',
      colWeight: 'Share',
      colOrigin: 'State',
      colReason: 'Why',
      confirmed: 'Confirmed',
      proposed: 'Proposed',
      confirm: 'Confirm',
      remove: 'Remove',
      what: 'What to link',
      material: 'Reference',
      materialPlaceholder: 'Search by name or code…',
      category: 'Family',
      metric: 'Internal figure',
      metricHint: 'So the market can be compared with your own numbers.',
      supplierId: 'Supplier identifier',
      supplierHint: 'So that supplier can be compared against this index.',
      weight: 'Share (0 to 1)',
      weightHint: 'How much of that reference\'s cost this indicator explains. A steel profile, 0.9; a finished valve, 0.3.',
      add: 'Add link',
      suggestLead: 'The model proposes; nothing is written until you accept. What you accept stays unconfirmed until you go through it one by one.',
      searchPlaceholder: 'Narrow the catalogue: steel, tube, profile…',
      suggest: 'Propose',
      suggestUnavailable: 'Proposals are not available on this instance. Links can still be added by hand.',
      acceptNote: 'What you accept is saved as a proposal. The exposure will say how many links are still unconfirmed.',
      accept: 'Accept {count}',
      accepted: '{count} links created',
      target: {
        material: 'Reference',
        category: 'Family',
        metric: 'Internal figure',
        supplier: 'Supplier',
      },
    },

    impacts: {
      title: 'Impacts',
      subtitle: 'What has moved outside, measured against yours',
      recompute: 'Recompute',
      recomputed: 'Recomputed: {count} impacts',
      status: 'Status',
      direction: 'Direction',
      risks: 'Risks',
      opportunities: 'Opportunities',
      exposure: 'Exposed spend',
      costAtRisk: 'Extra cost a year',
      savings: 'Saving a year',
      colWhat: 'What has moved',
      colChange: 'Change',
      colCost: 'A year',
      colReferences: 'References',
      colSeverity: 'Severity',
      colConfidence: 'Confidence',
      overDays: 'over {count} days',
      empty: 'No open impacts',
      emptyHint: 'Nothing you watch has moved far enough to cost money.',
    },

    impactStatus: {
      open: 'Open',
      reviewed: 'Reviewed',
      decided: 'Decided',
      dismissed: 'Dismissed',
      expired: 'Unwound',
      all: 'All',
    },

    impact: {
      subtitle: '{change} over {days} days',
      explain: 'Explain with AI',
      explainUnavailable: 'The explanation is not available right now. The figures above do not change.',
      dismiss: 'Dismiss',
      dismissConfirm: 'Dismiss this impact?',
      dismissConfirmHint: 'It stops counting on the panel. The figure and its trail stay, in case anybody asks.',
      decide: 'Record a decision',
      references: 'The references behind the figure',
      colReference: 'Reference',
      colShare: 'Share',
      colSpend: 'Annual spend',
      colDelta: 'Difference',
      decisions: 'Decisions taken',
      evidence: 'Where this comes from',
      state: 'State',
      status: 'Situation',
      raised: 'Detected',
      reviewed: 'Reviewed',
      dismissReason: 'Reason',
      openIndicator: 'Open the indicator',
    },

    insight: {
      what: 'What has happened',
      why: 'Why it matters',
      action: 'What can be done',
      overDays: 'over {count} days',
      touches: 'It affects {count} of your references',
      suppliers: '· {count} suppliers',
      exposure: 'Annual exposed spend',
      annualCost: 'Effect on the year',
    },

    evidence: {
      lead: 'Every figure this conclusion rests on, and where to look at it.',
      open: 'Open',
      none: 'The trail of this calculation was not kept.',
      days: '{count} days',
      move: 'Indicator movement',
      materials: 'References affected',
      annualSpend: 'Annual exposed spend',
      window: 'Window measured',
      confidence: 'Confidence of the calculation',
      source: 'Source of the figure',
      unconfirmed: 'Unconfirmed links',
      noData: 'References with no consumption or purchases',
    },

    simulate: {
      title: 'Simulate {name}',
      lead: 'It saves nothing. This is a question, and questions do not belong in the impact list.',
      custom: 'Another percentage',
      verdict: 'If {name} {change}, the year would cost {amount} {direction}.',
      more: 'more',
      less: 'less',
      exposure: 'Annual exposed spend',
      annualCost: 'Effect on the year',
      references: 'References affected',
      projected: 'Resulting price',
      unconfirmed: '{count} unconfirmed links',
      noData: '{count} references with no consumption or purchases',
      colReference: 'Reference',
      colShare: 'Share',
      colSpend: 'Annual spend',
      colDelta: 'Difference',
      suppliers: 'Who the conversation is with',
      noLinks: 'This indicator is not tied to any of your references.',
      noLinksHint: 'Without links there is nothing to multiply. The answer is a link, not a bigger number.',
      close: 'Close',
    },

    compare: {
      title: 'Market and company',
      subtitle: 'What the sector did, beside what you did',
      market: 'Market',
      company: 'Company',
      chart: 'Open',
      notLinked: 'No internal figure attached',
      link: 'Attach',
      empty: 'Nothing to compare yet',
      emptyHint: 'Tie an indicator to one of your internal figures from its own page.',
      noInside: 'No internal figures yet',
      noInsideBody: 'The right-hand column is filled by what Invoicing, Quotes and Purchasing report. Without those modules there is nothing to compare against.',
      pairTitle: '{indicator} and {metric}',
      correlation: '{strength} correlation (r = {r}).',
      lag: 'The lag that strengthens it most is {months} months.',
      notCausation: 'Two series moving together does not mean one causes the other.',
      tooFew: 'Only {count} comparable months; {min} are needed for the figure to mean anything.',
      noCorrelation: 'Not enough months in common to compute anything.',
    },

    suppliers: {
      title: 'Suppliers and the market',
      subtitle: 'How what you actually pay has moved',
      method: 'Compares the weighted average price of each reference across the two halves of the last {days} days.',
      window90: 'Three months',
      window180: 'Six months',
      window365: 'One year',
      window730: 'Two years',
      colSupplier: 'Supplier and reference',
      colBefore: 'First half',
      colAfter: 'Second half',
      colChange: 'Change',
      colSpend: 'Spend',
      tooFew: 'Bought once',
      empty: 'No purchases to compare yet',
      emptyHint: 'Every received purchase order adds lines here.',
      noPurchasing: 'Purchasing is not installed',
      noPurchasingBody: 'These figures come from received purchase orders. Without that module no line arrives.',
      caveat: 'Only lines from received orders count. A reference bought once inside the window has no percentage, and that is not the same as having held its price.',
    },

    reports: {
      title: 'Reports',
      subtitle: 'What the sector publishes, read and comparable',
      file: 'File a report',
      filed: 'Report filed',
      searchPlaceholder: 'Title or source…',
      allReports: 'All',
      onlyRead: 'Only the ones read',
      onlyUnread: 'Only the ones pending',
      colTitle: 'Report',
      colTier: 'Tier',
      colDate: 'Date',
      colFindings: 'What came out',
      colConfidence: 'Confidence',
      figures: '{count} figures',
      risks: '{count} risks',
      opportunities: '{count} opportunities',
      notRead: 'Not read',
      duplicate: 'Duplicate',
      confirmations: 'confirmed by {count}',
      empty: 'No reports yet',
      emptyHint: 'Upload the first PDF, or paste the text of a newsletter.',
      title2: 'Title',
      titlePlaceholder: 'Spanish industry report 2027',
      source: 'Source',
      noSource: 'No source',
      publishedAt: 'Publication date',
      publishedAtHint: 'The document\'s date, not today\'s.',
      document: 'Document',
      documentHint: 'PDF or photograph. Up to 25 MB.',
      orPaste: 'Or paste the text',
      orPasteHint: 'For a newsletter or a web page that cannot be attached.',
      save: 'File it',
      analyseLater: 'Filing does not read it. The AI reading is asked for afterwards, from the report\'s own page.',
    },

    report: {
      openFile: 'Open the document',
      read: 'Read with AI',
      reread: 'Read again',
      readDone: 'Report read',
      notRead: 'Not read yet',
      notReadBody: 'Nobody has asked for it to be read. A model reading sixty pages costs money, so it does not happen on its own.',
      notReadHint: 'Reading it produces the figures, the risks and the opportunities, each with the sentence it came from.',
      summary: 'Summary',
      figures: 'Figures',
      colFigure: 'What it measures',
      colValue: 'Value',
      colPeriod: 'Period',
      accept: 'Take into a series',
      accepted: 'In the series',
      risks: 'Risks',
      opportunities: 'Opportunities',
      predictions: 'Forecasts',
      about: 'About',
      source: 'Source',
      noSource: 'No source',
      published: 'Published',
      filed: 'Filed',
      confidence: 'Confidence',
      corroborations: 'The same, said by others',
      mentions: 'Raw materials mentioned',
    },

    accept: {
      title: 'Take the figure into a series',
      figure: 'Figure',
      period: 'Period',
      into: 'Into which indicator',
      intoHint: 'It is saved as one more reading of that series, with the report as its origin.',
      pick: 'Pick an indicator',
      confirm: 'Save into the series',
      done: 'Saved into the series',
    },

    topics: {
      title: 'Topics',
      subtitle: 'What you have decided to keep an eye on',
      new: 'New topic',
      edit: 'Edit topic',
      colName: 'Topic',
      colKind: 'Category',
      colImportance: 'Importance',
      colContents: 'Contents',
      colOwner: 'Owner',
      contents: '{sources} sources · {indicators} indicators',
      noOwner: 'No owner',
      empty: 'No topics yet',
      emptyHint: 'A topic groups sources, indicators and reports. Start with the three or four that genuinely affect you.',
      name: 'Name',
      namePlaceholder: 'Steel, Spanish construction, Automation…',
      description: 'Description',
      kind: 'Category',
      importance: 'Importance',
      owner: 'Owner',
      ownerHint: 'A topic nobody owns is a topic nobody updates.',
      archive: 'Archive',
      restore: 'Bring back',
      restored: 'Topic brought back',
      showArchived: 'Show archived',
      archiveConfirm: 'Archive this topic?',
      archiveConfirmHint: 'What hung off it stays where it is and reappears if you bring it back.',
    },

    sources: {
      title: 'Sources',
      subtitle: 'Where the figures come from, and whose word they are',
      new: 'New source',
      edit: 'Edit source',
      tiersTitle: 'What each tier means',
      searchPlaceholder: 'Name or address…',
      allTiers: 'All tiers',
      colName: 'Source',
      colTier: 'Tier',
      colKind: 'Kind',
      colChecked: 'Last checked',
      overdue: '{count} days',
      foundSomething: 'There was something',
      nothingNew: 'Nothing new',
      empty: 'No sources yet',
      emptyHint: 'Start with the three or four you already read every month.',
      name: 'Name',
      namePlaceholder: 'Statistics office · industrial price index',
      url: 'Address',
      urlPlaceholder: 'https://…',
      kind: 'Kind',
      tier: 'Reliability tier',
      frequency: 'How many days between checks',
      frequencyHint: 'Zero means "whenever something arrives": a newsletter has no calendar.',
      country: 'Country',
      countryPlaceholder: 'ES',
      notes: 'Notes',
      archive: 'Archive',
      restore: 'Bring back',
      restored: 'Source brought back',
      showArchived: 'Show archived',
      archiveConfirm: 'Archive this source?',
      archiveConfirmHint: 'The indicators and reports that name it go on naming it.',
    },

    alerts: {
      title: 'Alerts',
      subtitle: 'What deserves to interrupt somebody',
      new: 'New alert',
      edit: 'Edit alert',
      quietTitle: 'How the noise is kept down',
      quietBody: 'A rule fires ONCE per movement, not once a night. Then it keeps quiet for as many days as you tell it, even about something else: twenty notices about one thing are twenty notices nobody reads.',
      colName: 'Alert',
      colKind: 'Watches',
      colWho: 'Tells',
      colLast: 'Last fired',
      colState: 'State',
      neverFired: 'Never fired',
      on: 'On',
      off: 'Off',
      empty: 'No alerts yet',
      emptyHint: 'Without alerts the module still works: somebody has to come and look.',
      name: 'Name',
      namePlaceholder: 'Copper up more than 5%',
      kind: 'What it watches',
      indicator: 'Indicator',
      indicatorHint: 'Empty watches all of them.',
      indicatorSupplierHint: 'The index the supplier is compared against. One is required.',
      anyIndicator: 'Any',
      thresholdPct: 'From what percentage',
      thresholdEur: 'From how many euros a year',
      window: 'Over how many days',
      severity: 'Severity',
      quiet: 'Days of silence',
      quietHint: 'After firing, it keeps quiet for this long.',
      who: 'Which roles it tells',
      whoHint: 'Roles, not people: an alert addressed to somebody stops working the week they leave.',
      whoPlaceholder: 'admin, manager',
      active: 'Active',
      removeConfirm: 'Remove this alert?',
      removeConfirmHint: 'It stops firing. What it already sent stays in the notifications.',
      sentence: {
        price: 'When an indicator moves more than {threshold}% over {days} days. Silent for {quiet} days.',
        exposure: 'When an open impact passes {threshold} € a year. Silent for {quiet} days.',
        supplier: 'When a supplier rises more than {threshold} points above its index. Silent for {quiet} days.',
        report: 'When a new report arrives on the topic, looking at the last {days} days. Silent for {quiet} days.',
        trend: 'When an indicator changes direction by more than {threshold}%. Silent for {quiet} days.',
      },
    },

    alertKinds: {
      price: 'A price',
      priceHint: 'Fires when an indicator moves more than you say, over the window you say.',
      exposure: 'What it costs',
      exposureHint: 'Fires on the money, not the percentage: 2% on your main input is more than 12% on something you buy twice a year.',
      supplier: 'A supplier',
      supplierHint: 'Fires when a supplier rises above the index it is compared against. It needs that index linked.',
      report: 'A new report',
      reportHint: 'Fires when something new appears on a topic.',
      trend: 'A change of direction',
      trendHint: 'Fires when the month and the year stop pointing the same way.',
    },

    decisions: {
      title: 'Decisions',
      subtitle: 'What was decided, and whether it worked',
      new: 'Record a decision',
      allKinds: 'All',
      onlyPending: 'Only the ones due for review',
      colWhat: 'Decision',
      colKind: 'Kind',
      colWhen: 'Date',
      colOutcome: 'Outcome',
      reviewed: 'Reviewed',
      dueNow: 'Due for review',
      noReview: 'No review date',
      empty: 'No decisions yet',
      emptyHint: 'When management decides something from these figures, write it down here: it is the only way to find out later whether it worked.',
      about: 'About {name} {change} · {amount} a year',
      what: 'What is being decided',
      whatPlaceholder: 'Raise the structure tariff by 4%',
      kind: 'Kind',
      reason: 'Why',
      reasonHint: 'What was known that day. In a year, this is what gets read.',
      expected: 'What is expected to happen',
      expectedHint: 'This is what it will be judged against.',
      reviewAt: 'When to look again',
      reviewAtHint: 'With no date, nobody comes back.',
      willClose: 'Recording the decision also closes the impact that prompted it.',
      record: 'Record',
      recorded: 'Decision recorded',
    },

    decisionKinds: {
      update_prices: 'Update prices',
      negotiate_supplier: 'Negotiate with the supplier',
      buy_ahead: 'Buy ahead',
      change_material: 'Change material',
      review_quotes: 'Review quotes',
      hold_prices: 'Hold prices',
      raise_minimum: 'Raise the stock minimum',
      hedge: 'Hedge',
      other: 'Other',
    },

    decision: {
      subtitle: '{kind} · {when}',
      why: 'Why it was decided',
      noReason: 'No reason was written down.',
      expected: 'What was expected',
      outcome: 'How it went',
      noOutcome: 'Not reviewed yet.',
      howItWent: 'What actually happened',
      howItWentHint: 'With figures where there are any. This is what makes it possible to tell whether the module is any good.',
      recordOutcome: 'Save the outcome',
      outcomeRecorded: 'Outcome saved',
      basedOn: 'What it was decided on',
      about: 'About',
      kind: 'Kind',
      decidedAt: 'Date',
      decidedBy: 'Who',
      reviewAt: 'Review',
      openImpact: 'Open the impact',
    },

    briefing: {
      title: 'Management briefing',
      subtitle: 'What has happened, what it costs and what to review',
      weekly: 'Weekly report',
      meeting: 'Meeting briefing',
      write: 'Write with AI',
      unavailable: 'The writing is not available. The figures below are the report either way.',
      forDirection: 'For management',
      situation: 'Situation',
      risks: 'Risks',
      opportunities: 'Opportunities',
      suppliers: 'Suppliers',
      actions: 'What to do this week',
      noProse: 'No prose',
      noProseBody: 'The model could not write it. The figures below are complete.',
      risksCount: 'Open risks',
      opportunitiesCount: 'Opportunities',
      exposure: 'Exposed spend',
      costAtRisk: 'Extra cost a year',
      committed: 'Committed to suppliers',
      commodities: 'Raw materials',
      economy: 'Economy and sector',
      openRisks: 'Open risks',
      colWhat: 'What',
      colChange: 'Change',
      colCost: 'A year',
      colReferences: 'References',
      gaps: 'What this briefing does not cover',
      gapUnlinked: '{count} indicators are not tied to anything of yours: they are watched, but they produce no figure in euros.',
      gapStale: '{count} indicators have not been updated for a while.',
      gapCatalogue: 'There is no mirrored material catalogue: without Warehouse, a price movement cannot be turned into euros.',
      gapQuotes: {
        not_available: 'The margin of the open quote book is not in here: the costs of an unaccepted quote never leave the Quotes module, so this one does not know them.',
        module_absent: 'The margin of the open quote book is not in here: Quotes is not installed.',
      },
    },

    chart: {
      title: 'Series',
      empty: 'Not enough data',
      notZeroed: 'The axis does not start at zero',
    },

    related: {
      material: 'Market',
      supplier: 'Prices and market',
    },

    dashboard: {
      risks: 'Market risks',
      risksHelp: 'How many outside movements are costing money right now.',
      costAtRisk: 'Extra cost a year',
      costAtRiskHelp: 'What the open movements add to a year of cost, at current consumption.',
      opportunities: 'Opportunities',
      opportunitiesHelp: 'How many outside movements are in your favour: raw materials or energy falling.',
      exposure: 'Exposed spend',
      exposureHelp: 'The annual spend running through the references affected by something that has moved.',
      watched: 'Watched factors',
      watchedHelp: 'How many indicators are on the radar. It warns in amber when one has not been updated for a while.',
      moved: 'What is moving',
      movedHelp: 'The factors outside their normal range, with their change. Stretch the card and more fit.',
      reports: 'New reports',
      reportsHelp: 'What has arrived in the last fortnight, with the tier of its source.',
    },

    notifications: {
      category: 'Markets',
      price: {
        title: 'Movement in {indicator}',
        body: '{indicator} has moved {change}% over {days} days.',
      },
      trend: {
        title: 'Direction change in {indicator}',
        body: '{indicator} is at {change}% for the month and {year}% for the year: it has changed direction.',
      },
      exposure: {
        title: 'Extra cost from {indicator}',
        body: '{indicator} adds around {cost} € a year, across {materials} references.',
      },
      supplier: {
        title: '{supplier} is rising above the market',
        body: '{supplier} is {deviation} points above {indicator}.',
      },
      report: {
        title: 'New reports',
        body: '{count} reports have arrived. The latest: {title}.',
      },
      weekly: {
        title: 'Market briefing',
        body: '{risks} risks and {opportunities} opportunities. Estimated extra cost a year: {cost} €.',
      },
    },

    jobs: {
      assess: 'Recompute the market and evaluate the alerts',
      briefing: 'Weekly market briefing',
    },

    assistant: {
      recordingDecision: 'Recording the decision',
    },
  },

  permissions: {
    markets: {
      read: 'See the market: radar, indicators, reports and sources',
      exposure: 'See what the market costs us, in euros',
      write: 'Maintain indicators, series, links, sources and reports',
      decide: 'Record decisions, close impacts and configure alerts',
    },
  },

  settings: {
    ns: {
      markets: {
        title: 'Markets',
        subtitle: 'What counts as a lot in this company',
        watchThreshold: {
          label: 'Movement worth noticing (%, over 30 days)',
          help: 'Below this a movement is noise and nothing is recorded. Each indicator can carry its own; this is the one the rest use.',
        },
        alertThreshold: {
          label: 'Movement that counts as a lot (%, over 30 days)',
          help: 'From here the radar paints it as a risk and raises the severity of what it costs.',
        },
        impactFloor: {
          label: 'Smallest yearly cost worth recording (€)',
          help: 'A movement costing less than this a year is true and is not news. Without the floor, the list of things needing attention fills up with rows nobody will ever act on.',
        },
        highCost: {
          label: 'Yearly cost that makes an impact important (€)',
          help: 'What separates something to keep an eye on from something to take to a meeting.',
        },
        criticalCost: {
          label: 'Yearly cost that makes an impact critical (€)',
          help: 'Above this it is marked critical and whoever the alert names hears about it.',
        },
        weeklyBriefing: {
          label: 'Send the market briefing on Mondays',
          help: 'A summary of what moved, what it costs and what to look at. Turn it off if you would rather open the screen yourselves.',
        },
      },
    },
  },

  audit: {
    actions: {
      markets: {
        topic_created: 'Topic created',
        topic_updated: 'Topic edited',
        topic_archived: 'Topic archived',
        topic_restored: 'Topic brought back',
        source_created: 'Source created',
        source_updated: 'Source edited',
        source_checked: 'Source checked',
        source_archived: 'Source archived',
        source_restored: 'Source brought back',
        indicator_created: 'Indicator created',
        indicator_updated: 'Indicator edited',
        indicator_archived: 'Indicator archived',
        indicator_restored: 'Indicator brought back',
        point_recorded: 'Reading recorded',
        point_removed: 'Reading removed',
        series_imported: 'Series imported',
        link_created: 'Link created',
        link_updated: 'Link edited',
        link_removed: 'Link removed',
        links_suggested: 'AI-proposed links accepted',
        report_filed: 'Report filed',
        report_updated: 'Report edited',
        report_archived: 'Report withdrawn',
        impact_reviewed: 'Impact reviewed',
        impact_dismissed: 'Impact dismissed',
        alert_created: 'Alert created',
        alert_updated: 'Alert edited',
        alert_removed: 'Alert removed',
        decision_recorded: 'Decision recorded',
        decision_updated: 'Decision edited',
        decision_reviewed: 'Decision outcome recorded',
      },
    },
  },

  assistant: {
    openers: {
      markets: {
        'whats-changed': 'What has changed that could affect us?',
        'biggest-risk': 'What is our biggest market risk right now?',
        'supplier-above-market': 'Is any supplier raising prices above the market?',
        simulate: 'What would another rise in our main raw material cost us?',
      },
    },
  },
}
